import React, { useEffect, useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Loader2, Send, Calendar, MapPin, Users } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import { formatTime12h } from '../utils/timeFormat';

/**
 * <InviteTalentToShiftModal /> — Iter-186.
 *
 * Reusable individual-invite modal launched from any admin surface that
 * has a talent in context (Discover Talent, Talent Modal, User Detail,
 * Applicant list, Previous Workers, Favorites). Shows the next 14 days
 * of open shifts, lets the admin pick channels + add a custom message,
 * then dispatches via the same backend path used by mass invites — so
 * URLs stay canonical (Iter-185 helper) and failures get logged.
 *
 * Props:
 *   open: boolean
 *   onOpenChange: (next: boolean) => void
 *   talentUserId: string
 *   talentName?: string
 */
const InviteTalentToShiftModal = ({ open, onOpenChange, talentUserId, talentName, preselectedShift }) => {
  const [shifts, setShifts] = useState([]);
  const [loadingShifts, setLoadingShifts] = useState(false);
  const [selectedShiftId, setSelectedShiftId] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [sendInApp, setSendInApp] = useState(true);
  const [sendSms, setSendSms] = useState(false);
  const [customMessage, setCustomMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingShifts(true);
    // Iter-193 — when launched from a Shift Detail surface, lock the
    // picker to the live shift instead of defaulting to "first open
    // shift from the next-14-days list" (which used to surface fake
    // Miami Admin-Asst seeds).
    if (preselectedShift?.id) {
      setSelectedShiftId(String(preselectedShift.id));
    }
    api.get('/admin/shifts/open-next-14-days')
      .then(res => {
        if (cancelled) return;
        let rows = Array.isArray(res.data) ? res.data : [];
        if (preselectedShift?.id) {
          const exists = rows.some((r) => String(r.id) === String(preselectedShift.id));
          if (!exists) rows = [preselectedShift, ...rows];
        }
        setShifts(rows);
        if (preselectedShift?.id) {
          setSelectedShiftId(String(preselectedShift.id));
        } else if (!selectedShiftId && rows[0]) {
          setSelectedShiftId(rows[0].id);
        }
      })
      .catch(() => { if (!cancelled) toast.error('Could not load open shifts'); })
      .finally(() => { if (!cancelled) setLoadingShifts(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, preselectedShift]);

  const selectedShift = useMemo(
    () => shifts.find(s => s.id === selectedShiftId),
    [shifts, selectedShiftId],
  );

  const submit = async () => {
    if (!selectedShiftId || !talentUserId) return;
    if (!sendEmail && !sendInApp && !sendSms) {
      toast.error('Pick at least one channel');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post(
        `/admin/shifts/${selectedShiftId}/invite-talent/${talentUserId}`,
        {
          talent_user_id: talentUserId,
          send_email: sendEmail,
          send_in_app: sendInApp,
          send_sms: sendSms,
          custom_message: customMessage.trim() || null,
        },
      );
      const r = res.data || {};
      const s = r.sent || {};
      const f = r.failed || {};
      const totalSent = (s.email || 0) + (s.in_app || 0) + (s.sms || 0);
      const totalFailed = (f.email || 0) + (f.in_app || 0) + (f.sms || 0);
      if (totalSent === 0 && totalFailed > 0) {
        const reason = (r.failure_samples || [])[0]?.reason || 'send failed';
        toast.error(`Invite failed — ${reason}`, { duration: 7000 });
      } else if (totalFailed > 0) {
        toast.warning(
          `Partially sent — ${s.email} email · ${s.in_app} in-app · ${s.sms} SMS · ${totalFailed} failed`,
          { duration: 6000 },
        );
        onOpenChange(false);
      } else {
        toast.success(
          `Invite sent to ${talentName || 'talent'} — ${s.email || 0}E · ${s.in_app || 0}I · ${s.sms || 0}S`,
          { duration: 4000 },
        );
        onOpenChange(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to invite');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="invite-talent-to-shift-modal">
        <DialogHeader>
          <DialogTitle>Invite Selected Talent</DialogTitle>
          <DialogDescription>
            {talentName ? `Send ${talentName} a one-tap invite.` : 'Send a one-tap invite to this talent.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wide text-gray-500">Pick a gig (next 14 days)</Label>
            {loadingShifts ? (
              <div className="text-sm text-gray-500 flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading…
              </div>
            ) : shifts.length === 0 ? (
              <div className="text-sm text-gray-500 border rounded-md p-3 bg-gray-50" data-testid="no-open-shifts">
                No open shifts in the next 14 days.
              </div>
            ) : (
              <div className="max-h-56 overflow-y-auto space-y-1 border rounded-md p-1" data-testid="shift-picker">
                {shifts.map(s => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setSelectedShiftId(s.id)}
                    className={`w-full text-left p-2 rounded transition-colors ${
                      selectedShiftId === s.id ? 'bg-cyan-50 border border-cyan-300' : 'hover:bg-gray-50 border border-transparent'
                    }`}
                    data-testid={`shift-option-${s.id}`}
                  >
                    <p className="text-sm font-semibold text-gray-900 truncate">{s.event_title}</p>
                    <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                      <span className="flex items-center gap-0.5">
                        <Calendar className="w-3 h-3" /> {s.shift_date} · {formatTime12h(s.start_time)}
                      </span>
                      <span className="flex items-center gap-0.5 truncate">
                        <MapPin className="w-3 h-3" /> {s.city}, {s.state}
                      </span>
                      <span className="flex items-center gap-0.5">
                        <Users className="w-3 h-3" /> {s.spots_remaining}/{s.talent_needed}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-2 border-t pt-3">
            <Label className="text-xs uppercase tracking-wide text-gray-500">Channels</Label>
            <div className="space-y-2">
              {[
                { key: 'email', state: sendEmail, set: setSendEmail, label: 'Email' },
                { key: 'in_app', state: sendInApp, set: setSendInApp, label: 'In-app notification' },
                { key: 'sms', state: sendSms, set: setSendSms, label: 'SMS' },
              ].map(c => (
                <div key={c.key} className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">{c.label}</span>
                  <Switch
                    checked={c.state}
                    onCheckedChange={c.set}
                    data-testid={`channel-${c.key}-toggle`}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-2 border-t pt-3">
            <Label htmlFor="invite-message" className="text-xs uppercase tracking-wide text-gray-500">
              Custom message (optional)
            </Label>
            <Textarea
              id="invite-message"
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              placeholder="Add a quick personal note…"
              rows={3}
              maxLength={500}
              data-testid="invite-custom-message"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={submitting || !selectedShiftId}
            className="bg-cyan-500 hover:bg-cyan-600 text-white"
            data-testid="invite-submit-btn"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 animate-spin mr-1" />
            ) : (
              <Send className="w-4 h-4 mr-1" />
            )}
            Send Invite
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InviteTalentToShiftModal;
